import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ENDPOINT } from "../../../endpoints/endpoints";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LeagueCreate = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    image:"",
  });

  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // 🔹 Token Checking
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // 🔹 Handle Text Change
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // 🔹 Handle Image Select / Drop
  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    } else {
      toast.error("Please upload a valid image file!");
    }
  };

  // 🔹 Handle Input Change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // 🔹 Drag & Drop Events
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // 🔹 Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("You must log in first!");
        navigate("/login");
        return;
      }

      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });
      if (image) data.append("image", image);

      const response = await axios.post(ENDPOINT.LEAGUECATEGORY_CREATE, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const msg = response.data.message || "Unknown response";
      if (response.data) {
        toast.success(msg);
        setTimeout(() => navigate("/league/list", { state: { toastMessage: msg } }), 1200);
      } else {
        toast.error(msg);
      }
    } catch (err) {
      console.error("Error creating league:", err);
      const backendMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Something went wrong.";
      toast.error(backendMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={600} mb={3}>
          🖼 Create New League 
        </Typography>

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <Stack spacing={2}>
            {/* English Title */}
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />

              {/* 🖼 Drag and Drop Zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: dragActive ? "#f0f7ff" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={() => document.getElementById("fileInput").click()}
            >
              {preview ? (
                <Box>
                  <Box
                    component="img"
                    src={preview}
                    alt="Preview"
                    sx={{
                      width: 200,
                      height: 200,
                      objectFit: "cover",
                      borderRadius: 2,
                      border: "1px solid #ddd",
                    }}
                  />
                  <Typography variant="body2" mt={1} color="text.secondary">
                    Click or drop a new image to replace
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Drag & drop soluction image here, or click to select file
                </Typography>
              )}
              <input
                id="fileInput"
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Box>

            {/* Submit Button */}
            <Box textAlign="right" mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={loading}
                sx={{ px: 4, textTransform: "none" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Create League"}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default LeagueCreate;
